import React, { Component, Fragment } from 'react';
import { compose } from 'recompose';
import classNames from 'classnames';
import { MutationFunction } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import { Button, Grid, Typography } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { Trans } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { FeedKey, GetFollowedDocument, UpdateUserProfileComponent } from 'api';
import { FOLLOW_QUERIES_LIMIT } from 'constants/index';
import {
  ActionContainer,
  ClientLogo,
  Error,
  FollowToggle,
  SubmitButton,
  Layouts,
  IFollowToggleRenderProps,
} from 'components';
import { index } from 'paths';
import {
  withUser,
  withErrorHandler,
  withFollowed,
  IWithUser,
  IWithErrorHandler,
  IWithFollowed,
} from 'providers';
import { withUserClientFollowMappings, IWithUserClientFollowMappings } from 'providers/queries';

import { GroupCard, UserCard } from '../components';

interface IPropsEnhanced
  extends RouteComponentProps,
    IWithUser,
    IWithUserClientFollowMappings,
    IWithFollowed,
    IWithErrorHandler,
    WithStyles<typeof styles> {}

interface IState {
  toggling: boolean;
  working: boolean;
  followCount: number;
}

const REQUIRED_FOLLOW_COUNT = 3;

const styles = ({ breakpoints, custom, spacing, typography }: Theme) =>
  createStyles({
    chooseButton: {
      width: 250,
      '&:disabled': {
        color: custom.palette.green,
      },
    },
    main: {
      paddingTop: spacing(10),
      paddingBottom: spacing(10),

      [breakpoints.down('md')]: {
        padding: spacing(3),
      },
    },
    mainLeft: {
      [breakpoints.up('md')]: {
        paddingRight: spacing(4),
        paddingLeft: spacing(10),
      },
    },
    mainRight: {
      [breakpoints.up('md')]: {
        paddingLeft: spacing(4),
        paddingRight: spacing(10),
      },
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(
  withUser,
  withUserClientFollowMappings,
  withFollowed('followed'),
  withErrorHandler,
  withRouter,
  withStyles(styles),
);

class Follows extends Component<IPropsEnhanced, IState> {
  state: IState = {
    toggling: false,
    working: false,
    followCount: 0,
  };

  handleCompleted = () => {
    const { history } = this.props;

    this.setState({ working: false }, () => {
      history.push(index);
    });
  };

  handleError = (error: ApolloError) => {
    const { onError } = this.props;
    this.setState({ working: false }, () => onError(error));
  };

  handleFollowToggle = (followToggleProps: IFollowToggleRenderProps) => () => {
    const { followCount } = this.state;
    const { isFollowing, onClick } = followToggleProps;

    const updatedFollowCount = isFollowing ? followCount - 1 : followCount + 1;
    this.setState({ followCount: updatedFollowCount }, onClick);
  };

  onFinish = (updateUserProfile: MutationFunction) => () => {
    const { user } = this.props;

    const variables = {
      data: {
        id: user.id,
        last_onboarding_step: 2,
        onboarding_completed: true,
      },
    };

    this.setState({ working: true });

    return updateUserProfile({
      variables,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetFollowedDocument,
          variables: { data: { user_id: user.id, limit: FOLLOW_QUERIES_LIMIT } },
        },
      ],
    });
  };

  renderGroups() {
    const { classes, followMappings } = this.props;
    const { suggested_groups } = followMappings;

    const mainClasses = classNames(classes.main, classes.mainLeft);

    return (
      <div className={mainClasses}>
        <Typography paragraph variant="h4">
          <Trans i18nKey="common:group_plural" />
        </Typography>
        {suggested_groups.map(group => (
          <FollowToggle key={group.id} source={group} type={FeedKey.Group}>
            {props => (
              <GroupCard
                group={group}
                isFollowing={props.isFollowing}
                onClick={this.handleFollowToggle(props)}
              />
            )}
          </FollowToggle>
        ))}
      </div>
    );
  }

  renderUsers() {
    const { classes, followMappings } = this.props;
    const { suggested_users } = followMappings;
    const mainClasses = classNames(classes.main, classes.mainRight);

    return (
      <div className={mainClasses}>
        <Typography paragraph variant="h4">
          <Trans i18nKey="common:person_plural" />
        </Typography>
        {suggested_users.map(suggestedUser => (
          <FollowToggle key={suggestedUser.id} source={suggestedUser} type={FeedKey.User}>
            {props => (
              <UserCard
                user={suggestedUser}
                isFollowing={props.isFollowing}
                onClick={this.handleFollowToggle(props)}
              />
            )}
          </FollowToggle>
        ))}
      </div>
    );
  }

  renderSidebar() {
    const { classes, error, onErrorClose } = this.props;
    const { followCount, working } = this.state;

    const needsFollows = followCount < REQUIRED_FOLLOW_COUNT;
    const followsNeeded = REQUIRED_FOLLOW_COUNT - followCount;

    return (
      <Fragment>
        <ClientLogo />
        <Typography color="textSecondary" gutterBottom variant="h4">
          <Trans i18nKey="onboarding:sidebarGroupsHeader" />
        </Typography>
        <Typography gutterBottom variant="subtitle2">
          <Trans
            i18nKey="onboarding:sidebarGroupsSubHeader"
            values={{ followCount: REQUIRED_FOLLOW_COUNT }}
          />
        </Typography>

        <ActionContainer>
          <UpdateUserProfileComponent onError={this.handleError} onCompleted={this.handleCompleted}>
            {updateUserProfile => {
              return needsFollows ? (
                <Button disabled variant="outlined" size="large" className={classes.chooseButton}>
                  <Trans
                    i18nKey="onboarding:sidebarGroupsButtonCount"
                    values={{ followCount: followsNeeded }}
                  />
                </Button>
              ) : (
                <Fragment>
                  <Error key={error} message={error} onClose={onErrorClose} />
                  <SubmitButton
                    working={working}
                    onClick={this.onFinish(updateUserProfile)}
                    text={<Trans i18nKey="onboarding:sidebarFollowsFinish" />}
                  />
                </Fragment>
              );
            }}
          </UpdateUserProfileComponent>
        </ActionContainer>
      </Fragment>
    );
  }

  renderMain = () => (
    <Grid container>
      <Grid item xs={12} sm={6}>
        {this.renderGroups()}
      </Grid>
      <Grid item xs={12} sm={6}>
        {this.renderUsers()}
      </Grid>
    </Grid>
  );

  render() {
    return (
      <Layouts.Full2Column
        sticky
        offset={80}
        sidebar={this.renderSidebar()}
        main={this.renderMain()}
      />
    );
  }
}

export default enhancers(Follows);
